import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Search, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function TicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [form, setForm] = useState({ title: '', description: '', category: 'general', priority: 'medium' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadTickets(); }, []);

  const loadTickets = async () => {
    try {
      const res = await api.get('/admin/tickets');
      setTickets(res.data);
    } catch { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  };

  const createTicket = async () => {
    if (!form.title || !form.description) { toast.error('Fill in all fields'); return; }
    setSubmitting(true);
    try {
      await api.post('/admin/tickets', form);
      toast.success('Ticket created');
      setShowNew(false);
      setForm({ title: '', description: '', category: 'general', priority: 'medium' });
      loadTickets();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const updateStatus = async (ticketId, status) => {
    try {
      await api.put(`/admin/tickets/${ticketId}`, { status });
      toast.success('Status updated');
      loadTickets();
    } catch { toast.error('Failed to update'); }
  };

  const deleteTicket = async (ticketId) => {
    try {
      await api.delete(`/admin/tickets/${ticketId}`);
      toast.success('Ticket deleted');
      loadTickets();
    } catch { toast.error('Failed to delete'); }
  };

  const filtered = tickets.filter(t => {
    const matchSearch = t.title.toLowerCase().includes(search.toLowerCase()) || t.description?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || t.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const statusColor = (s) => ({ open: 'bg-amber-50 text-amber-700 border-amber-100', in_progress: 'bg-blue-50 text-blue-700 border-blue-100', resolved: 'bg-emerald-50 text-emerald-700 border-emerald-100', closed: 'bg-slate-100 text-slate-600' }[s] || 'bg-slate-100 text-slate-600');
  const priorityColor = (p) => ({ urgent: 'bg-red-50 text-red-700', high: 'bg-orange-50 text-orange-700', medium: 'bg-amber-50 text-amber-700', low: 'bg-emerald-50 text-emerald-700' }[p] || 'bg-slate-100 text-slate-600');

  if (loading) return <div className="flex items-center justify-center h-64"><div className="loading-spinner" /></div>;

  return (
    <div data-testid="tickets-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Maintenance Tickets</h1>
          <p className="text-sm text-slate-500 mt-1">{tickets.length} total tickets</p>
        </div>
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid="new-ticket-btn"><Plus size={14} /> New Ticket</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-['Outfit']">Create Ticket</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div><Label className="text-xs mb-1 block">Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Describe the issue" data-testid="ticket-title-input" /></div>
              <div><Label className="text-xs mb-1 block">Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Details..." rows={3} data-testid="ticket-desc-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs mb-1 block">Category</Label>
                  <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                    <SelectTrigger data-testid="ticket-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['general','maintenance','plumbing','electrical','security','cleaning'].map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Priority</Label>
                  <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                    <SelectTrigger data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['low','medium','high','urgent'].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={createTicket} disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-ticket-btn">
                {submitting ? <Loader2 size={14} className="animate-spin mr-2" /> : null} Create Ticket
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tickets..." className="pl-9 text-sm" data-testid="ticket-search" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[140px] text-sm" data-testid="ticket-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-100">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Title</TableHead>
                <TableHead className="text-xs">Category</TableHead>
                <TableHead className="text-xs">Priority</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs">Created</TableHead>
                <TableHead className="text-xs w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-400 py-8">No tickets found</TableCell></TableRow>
              )}
              {filtered.map(t => (
                <TableRow key={t.ticket_id} data-testid={`ticket-row-${t.ticket_id}`}>
                  <TableCell>
                    <p className="text-sm font-medium text-slate-900">{t.title}</p>
                    <p className="text-xs text-slate-500 truncate max-w-[200px]">{t.description}</p>
                  </TableCell>
                  <TableCell><Badge variant="secondary" className="text-xs capitalize">{t.category}</Badge></TableCell>
                  <TableCell><Badge className={`text-xs capitalize ${priorityColor(t.priority)}`}>{t.priority}</Badge></TableCell>
                  <TableCell>
                    <Select value={t.status} onValueChange={v => updateStatus(t.ticket_id, v)}>
                      <SelectTrigger className="h-7 text-xs w-[110px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['open','in_progress','resolved','closed'].map(s => <SelectItem key={s} value={s} className="text-xs capitalize">{s.replace('_',' ')}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{new Date(t.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => deleteTicket(t.ticket_id)} className="text-red-500 hover:text-red-700 h-7 w-7 p-0" data-testid={`delete-ticket-${t.ticket_id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
